import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { Stream, StreamReport } from '../models/stream.model';
import { environment } from '../../environments/environment';
import { CacheableHttpService } from '../shared/services/cacheable-http.service';
import { SecureStorageService } from './secure-storage.service';

interface ApiResponse {
  status: number;
  data: Stream[];
}

@Injectable({
  providedIn: 'root'
})
export class StreamService {
  apiUrl: string = environment.apiUrl;
  private token: string = '';
  private httpOptions: { headers: HttpHeaders } = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': ''
    })
  };
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  
  constructor(
    private http: HttpClient,
    private cacheHttp: CacheableHttpService,
    private secureStorage: SecureStorageService
  ) {
    this.token = this.secureStorage.getItem<string>('token') || '';
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      })
    };
  }

  private getHeaders() {
    const token = this.secureStorage.getItem<string>('token') || '';
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      })
    };
  }

  /**
   * Récupère tous les streams avec mise en cache
   * @param refresh Forcer le rafraîchissement du cache
   * @returns Observable<Stream[]>
   */
  getAllStreams(refresh: boolean = false): Observable<Stream[]> {
    const url = `${this.apiUrl}/admin/allStreams`;
    return this.cacheHttp.get<ApiResponse>(url, {
      ttl: this.CACHE_TTL,
      refresh,
      shareReplay: 1,
      httpOptions: this.httpOptions
    }).pipe(
      map(response => response.data)
    );
  }

  /**
   * Met à jour le statut d'un stream et invalide le cache
   */
  updateStreamStatus(streamId: number, isPublic: boolean): Observable<any> {
    const url = `${this.apiUrl}/admin/streams/${streamId}/status`;
    return this.http.post(url, {
      is_public: isPublic
    }, this.httpOptions).pipe(
      tap(() => {
        // Invalider le cache des streams après la mise à jour
        this.invalidateStreamsCache();
      })
    );
  }

  /**
   * Signaler un live (fan authentifié).
   */
  reportStream(
    slug: string,
    body: { reason: string; details?: string }
  ): Observable<{ status: number; message?: string }> {
    const url = `${this.apiUrl}/streams/${encodeURIComponent(slug)}/reports`;
    return this.http.post<{ status: number; message?: string }>(url, body, this.getHeaders());
  }

  /**
   * Liste des signalements pour un stream (admin).
   */
  getStreamReports(
    streamId: number,
    page: number = 1,
    perPage: number = 15,
    reason?: string
  ): Observable<{
    status: number;
    data: StreamReport[];
    pagination: {
      current_page: number;
      per_page: number;
      total: number;
      last_page: number;
      from: number | null;
      to: number | null;
    };
  }> {
    const url = `${this.apiUrl}/admin/streams/${streamId}/reports`;
    let params = new HttpParams()
      .set('page', String(page))
      .set('per_page', String(perPage));
    if (reason != null && reason !== '') {
      params = params.set('reason', reason);
    }
    return this.http.get<{
      status: number;
      data: StreamReport[];
      pagination: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
        from: number | null;
        to: number | null;
      };
    }>(url, { ...this.getHeaders(), params });
  }

  /**
   * Supprime un stream et invalide le cache
   */
  deleteStream(streamId: number): Observable<any> {
    const url = `${this.apiUrl}/admin/streams/${streamId}`;
    return this.http.delete(url, this.httpOptions).pipe(
      tap(() => {
        // Invalider le cache des streams après la suppression
        this.invalidateStreamsCache();
      })
    );
  }

  /**
   * Invalide le cache des streams
   */
  private invalidateStreamsCache(): void {
    // Invalider toutes les URLs contenant "streams" ou "allStreams"
    this.cacheHttp.invalidateCachePattern(/streams|allStreams/);
  }
} 